"use client";

import * as React from "react";
import { ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogCloseButton,
  DialogHeaderCentered,
} from "@shared/src/components/ui/dialog";
import { cn } from "@shared/src/utils/cn";
import { type VariantProps } from "class-variance-authority";

type HeaderVariant = "centered" | "inline";

// Use the enhanced dialog variants instead of our own
type DialogVariants = VariantProps<typeof DialogContent>;

interface CustomDialogueProps extends Partial<Pick<DialogVariants, "size" | "padding" | "background">> {
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  description?: string;
  message?: string;
  children: (closeModal: () => void) => React.ReactNode;
  dialogueContentClassName?: string;
  dialogueHeaderClassName?: string;
  headerVariant?: HeaderVariant;
  headerAction?: React.ReactNode;
  // Back button props
  onBack?: () => void;
  backButtonDisabled?: boolean;
  // Left button props
  leftButton?: React.ReactNode;
  leftButtonAction?: () => void;
  leftButtonClassName?: string;
  showLeftButton?: boolean;
  // Close button props
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  startTitle?: boolean;
  preventClose?: {
    enabled: boolean;
    hideCloseButton?: boolean;
  };
}

const CustomDialogue: React.FC<CustomDialogueProps> = ({
  trigger,
  title,
  description,
  message,
  children,
  dialogueContentClassName,
  dialogueHeaderClassName,
  headerVariant = "inline",
  headerAction,
  // Back button props
  onBack,
  backButtonDisabled = true,
  // Left button props
  leftButton,
  leftButtonAction,
  leftButtonClassName,
  showLeftButton = false,
  // Close button props
  open: controlledOpen,
  onClose,
  onOpenChange,
  startTitle = false,
  preventClose,
  // Dialog variant props
  size = "lg",
  padding = "md",
  background = "primary",
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!contentRef.current) return;

    const handleFocus = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    };

    const content = contentRef.current;
    content.addEventListener("focusin", handleFocus);
    return () => content.removeEventListener("focusin", handleFocus);
  }, []);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (newOpen: boolean) => {
    setUncontrolledOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // If trying to close (newOpen is false) and preventClose is enabled
    if (!newOpen && preventClose?.enabled) {
      // Only allow closing if it's from the close button (hideCloseButton is false)
      if (!preventClose.hideCloseButton) {
        setOpen(false);
        onClose?.();
      }
      return;
    }
    setOpen(newOpen);
    if (!newOpen) {
      onClose?.();
    }
  };

  const handleLeftButtonClick = () => {
    leftButtonAction?.();
  };

  const closeModal = () => {
    handleOpenChange(false);
  };

  const renderHeader = () => {
    // For centered variant, use the centralized DialogHeaderCentered component
    if (headerVariant === "centered") {
      const leftButtonElement =
        showLeftButton && leftButton ? (
          <button onClick={handleLeftButtonClick} className={cn("w-fit", leftButtonClassName)}>
            {leftButton}
          </button>
        ) : null;

      return (
        <div className={cn("pb-0", dialogueHeaderClassName)}>
          {leftButtonElement && <div className="flex justify-start mb-2">{leftButtonElement}</div>}
          <DialogHeaderCentered
            title={title as string}
            description={description}
            onClose={preventClose?.hideCloseButton ? undefined : () => handleOpenChange(false)}
          />
        </div>
      );
    }

    // Inline variant - keep the custom logic for backwards compatibility
    const backButtonElement =
      backButtonDisabled || !onBack ? null : (
        <button onClick={onBack} className={cn("text-text-tertiary group w-fit hover:text-text-primary")}>
          <ArrowLeft className="w-10 h-10 rounded-full p-2 group-hover:border border-surfaces-bg-tertiary" />
        </button>
      );
    const closeButton = preventClose?.hideCloseButton ? null : (
      <DialogCloseButton onClose={() => handleOpenChange(false)} />
    );

    const leftButtonElement =
      showLeftButton && leftButton ? (
        <button onClick={handleLeftButtonClick} className={cn("w-fit", leftButtonClassName)}>
          {leftButton}
        </button>
      ) : null;

    return (
      <div className={cn("pb-0", dialogueHeaderClassName)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {backButtonElement}
            {leftButtonElement}
          </div>
          <h6 className={cn("text-text-primary font-extrabold", startTitle ? "text-start" : "text-center flex-1")}>
            {title}
          </h6>
          {closeButton}
        </div>
        <DialogTitle className="sr-only">{typeof title === "string" ? title : "Dialog"}</DialogTitle>
        {description && <p className="text-center text-text-secondary text-small-2">{description}</p>}
      </div>
    );
  };

  const messageElement = message && <p className="text-center text-text-secondary text-small-2 mt-5">{message}</p>;

  return (
    <Dialog modal={false} open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        size={size}
        padding={padding}
        background={background}
        position="center-mobile-bottom"
        hideCloseButton={true}
        height="responsive"
        layout="flex-col-overflow"
        scroll
        onPointerDownOutside={(event: CustomEvent) => {
          if (preventClose?.enabled) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(e: KeyboardEvent) => {
          if (preventClose?.enabled) {
            e.preventDefault();
          }
        }}
        className={cn("gap-0", dialogueContentClassName)}
      >
        <DialogHeader className="shrink-0 mb-2.5">{renderHeader()}</DialogHeader>
        <div className="flex-1 overflow-y-scroll overscroll-y-contain custom-scrollbar pb-2">
          {children(closeModal)}
          {messageElement}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDialogue;
